import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Container,
  Step,
  StepLabel,
  Stepper,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
  useTheme,
  alpha,
  styled,
  Chip
} from '@mui/material';
import {
  ArrowBack,
  Check,
  Upload,
  Person,
  Close,
  InsertDriveFile
} from '@mui/icons-material';
import { useToast } from '../context/ToastContext';
import { useDatabaseMode } from '../context/DatabaseModeContext';
import { useWorkflowNavigation } from '../hooks/useWorkflowNavigation';
import ClientFileUpload from '../components/ClientFileUpload';
import clientSimilarityService, { SimilarityMatch } from '../services/client/clientSimilarityService';
import workflowService from '../services/api/workflowService';
import blobService from '../services/storage/blobService';
import clientFilesService, { ClientFile } from '../services/clientFilesService';

// Types
interface DatabaseClient {
  client_id: number;
  name: string;
  client_code?: string;
  domains?: string;
  active?: boolean;
}

interface ClientKeywordSetupScreenProps {
  onComplete?: (client: any, files: any[]) => void;
  onCancel?: () => void;
}

// Styled components
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: 'blur(8px)',
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
  backdropFilter: 'blur(8px)',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const GradientTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
})) as typeof Typography;

const steps = ['Client Information', 'Upload Data Sources', 'Review & Complete'];

const ClientKeywordSetupScreen: React.FC<ClientKeywordSetupScreenProps> = ({
  onComplete,
  onCancel
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isUsingDatabaseApproach } = useDatabaseMode();
  const { openWorkflow } = useWorkflowNavigation();

  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [clientName, setClientName] = useState('');
  const [clientDomains, setClientDomains] = useState('');
  const [similarityMatches, setSimilarityMatches] = useState<SimilarityMatch[]>([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [createdClient, setCreatedClient] = useState<DatabaseClient | null>(null);
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<ClientFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [createdWorkflow, setCreatedWorkflow] = useState<{ workflow_id: string; workflow_name: string } | null>(null);
  const [isCreatingWorkflow, setIsCreatingWorkflow] = useState(false);

  // Database approach is now managed by context

  // Utility function to format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle client name change with duplicate detection
  const handleClientNameChange = useCallback((value: string) => {
    setClientName(value);
    
    if (value.trim().length > 2) {
      // For this demo, we'll use empty array since we don't have existing clients loaded
      // In real implementation, this would fetch from the database or blob storage
      const existingClientNames: string[] = [];
      
      const matches = clientSimilarityService.checkForDuplicates(value, existingClientNames);
      setSimilarityMatches(matches);
      setShowDuplicateWarning(matches.length > 0);
    } else {
      setSimilarityMatches([]);
      setShowDuplicateWarning(false);
    }
  }, []);



  // Create client
  const handleCreateClient = useCallback(async () => {
    if (!clientName.trim()) {
      showToast('Please enter a client name', 'error');
      return null;
    }

    try {
      setIsCreatingClient(true);

      if (isUsingDatabaseApproach) {
        const clientCode = clientName
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '');

        const processedDomains = clientDomains.trim() || undefined;

        const newClient = await workflowService.createDbClient(
          clientName,
          clientCode,
          true,
          undefined, // industry parameter
          processedDomains
        );

        localStorage.setItem('db_client_id', newClient.client_id.toString());
        setCreatedClient(newClient);
        return newClient; // Return the created client data

      } else {
        const clientId = clientName
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '');

        const newClient = {
          client_id: 0, // placeholder for blob approach
          name: clientName,
          client_code: clientId
        };

        localStorage.setItem('selected_client_id', clientId);
        setCreatedClient(newClient);
        return newClient; // Return the created client data
      }
    } catch (error) {
      console.error('Error creating client:', error);
      showToast('Failed to create client', 'error');
      return null;
    } finally {
      setIsCreatingClient(false);
    }
  }, [clientName, clientDomains, isUsingDatabaseApproach, showToast]);

  // Create workflow for file uploads
  const handleCreateWorkflow = useCallback(async (clientForWorkflow?: any) => {
    const clientToUse = clientForWorkflow || createdClient;
    
    if (!clientToUse) {
      showToast('Client must be created first', 'error');
      return false;
    }
    
    try {
      setIsCreatingWorkflow(true);
      
      const workflowName = `${clientName} Analysis`;
      let workflowId: string | number;

      if (isUsingDatabaseApproach && clientToUse.client_id > 0) {
        const dbWorkflow = await workflowService.createDbWorkflow(
          clientToUse.client_id,
          workflowName,
          'Created from Client & Keyword Setup Interface'
        );
        workflowId = dbWorkflow.workflow_id;
      } else {
        // Generate workflow ID for blob approach
        workflowId = `workflow_${Date.now()}`;
      }

      const workflowData = { 
        workflow_id: workflowId.toString(), 
        workflow_name: workflowName 
      };
      
      setCreatedWorkflow(workflowData);
      
      return workflowData;
      
    } catch (error) {
      console.error('Error creating workflow:', error);
      showToast('Failed to create workflow', 'error');
      return false;
    } finally {
      setIsCreatingWorkflow(false);
    }
  }, [createdClient, clientName, isUsingDatabaseApproach, showToast]);

  // Load uploaded files for review step
  const loadUploadedFiles = useCallback(async () => {
    if (!createdClient || !createdWorkflow) return;
    
    try {
      setLoadingFiles(true);
      const files = await clientFilesService.getClientWorkflowFiles(
        createdClient.client_id.toString(), 
        createdWorkflow.workflow_id
      );
      setUploadedFiles(files);
    } catch (error) {
      console.error('Error loading uploaded files:', error);
      showToast('Failed to load uploaded files', 'error');
    } finally {
      setLoadingFiles(false);
    }
  }, [createdClient, createdWorkflow, showToast]);

  // Handle next step
  const handleNext = useCallback(async () => {
    if (activeStep === 0) {
      // Client information step
      let currentClient = createdClient;
      let clientWasCreated = false;
      
      // Only create client if it doesn't exist yet
      if (!createdClient) {
        currentClient = await handleCreateClient();
        clientWasCreated = !!currentClient;
      }
      
      if (currentClient) {
        // Only create workflow if it doesn't exist yet
        let workflowResult: any = true;
        let workflowWasCreated = false;
        
        if (!createdWorkflow) {
          workflowResult = await handleCreateWorkflow(currentClient);
          workflowWasCreated = !!workflowResult;
        }
        
        if (workflowResult) {
          // Show combined success message when both are created
          if (clientWasCreated && workflowWasCreated) {
            const clientDisplayName = currentClient.name || clientName;
            const clientId = currentClient.client_id || 'N/A';
            const workflowName = workflowResult.workflow_name || `${clientName} Analysis`;
            const workflowId = workflowResult.workflow_id || 'N/A';
            
            showToast(
              `Client "${clientDisplayName}" (${clientId}) and Workflow "${workflowName}" (${workflowId}) created successfully!`, 
              'success'
            );
          }
          
          setActiveStep(prev => prev + 1);
        }
      }
    } else if (activeStep === 1) {
      // File upload step - load files for review
      await loadUploadedFiles();
      setActiveStep(prev => prev + 1);
    } else if (activeStep === 2) {
      // Final step - navigate to workflow dashboard
      handleCompleteSetup();
    }
  }, [activeStep, handleCreateClient, handleCreateWorkflow, loadUploadedFiles, createdClient, createdWorkflow, clientName]);

  // Handle back step
  const handleBack = useCallback(() => {
    setActiveStep(prev => prev - 1);
  }, []);

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/start-process');
    }
  }, [navigate, onCancel]);

  // Complete setup and navigate to workflow dashboard
  const handleCompleteSetup = useCallback(async () => {
    if (!createdClient || !createdWorkflow) {
      showToast('Client and workflow must be created first', 'error');
      return;
    }

    try {
      setIsProcessing(true);

      // Store workflow ID in localStorage for context
      if (isUsingDatabaseApproach) {
        localStorage.setItem('db_workflow_id', createdWorkflow.workflow_id);
      } else {
        localStorage.setItem('selected_workflow_id', createdWorkflow.workflow_id);
      }

      // Navigate to workflow dashboard using smart navigation hook
      const clientId = isUsingDatabaseApproach 
        ? createdClient.client_id
        : createdClient.client_code || '';

      if (onComplete) {
        onComplete(createdClient, uploadedFiles);
      } else {
        // Use the workflow navigation hook to automatically detect v1 vs v2
        await openWorkflow({
          clientId,
          workflowId: createdWorkflow.workflow_id
        });
      }

      showToast('Setup completed successfully! Redirecting to workflow dashboard...', 'success');

    } catch (error) {
      console.error('Error completing setup:', error);
      showToast('Failed to complete setup', 'error');
    } finally {
      setIsProcessing(false);
    }
  }, [createdClient, createdWorkflow, isUsingDatabaseApproach, openWorkflow, onComplete, uploadedFiles]);

  // Check if current step is complete
  const isStepComplete = (step: number) => {
    switch (step) {
      case 0:
        return !!createdClient && !!createdWorkflow;
      case 1:
        return step < activeStep;
      case 2:
        return false; // Final step
      default:
        return false;
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return clientName.trim().length > 0 && !showDuplicateWarning && !isCreatingClient && !isCreatingWorkflow;
      case 1:
        return true; // Can proceed even without files for MVP
      case 2:
        return true;
      default:
        return false;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* App Bar */}
      <StyledAppBar position="sticky" color="default" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            onClick={handleCancel}
            disabled={isProcessing}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          
          <Typography variant="h6" sx={{ flexGrow: 1, color: 'text.secondary', fontWeight: 600 }}>
            Client & Keyword Setup
          </Typography>

          <Button
            variant="outlined"
            startIcon={<Close />}
            onClick={handleCancel}
            disabled={isProcessing}
            sx={{ borderRadius: '8px' }}
          >
            Cancel
          </Button>
        </Toolbar>
        
        {isProcessing && <LinearProgress />}
      </StyledAppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Stepper */}
        <StyledPaper sx={{ p: 3, mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label} completed={isStepComplete(index)}>
                <StepLabel
                  StepIconComponent={({ active, completed }) => (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: completed
                          ? 'success.main'
                          : active
                          ? 'primary.main'
                          : 'grey.300',
                        color: completed || active ? 'white' : 'grey.500',
                      }}
                    >
                      {completed ? <Check fontSize="small" /> : index + 1}
                    </Box>
                  )}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </StyledPaper>

        {/* Step Content */}
        <StyledPaper sx={{ p: 4, mb: 3 }}>
          {activeStep === 0 && (
            <Box>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Client Information
              </Typography>
              
              <TextField
                label="Client Name"
                fullWidth
                value={clientName}
                onChange={(e) => handleClientNameChange(e.target.value)}
                disabled={isCreatingClient}
                error={showDuplicateWarning}
                sx={{ mb: 2 }}
                helperText={
                  showDuplicateWarning
                    ? `Potential duplicate found! ${similarityMatches.length} similar client(s) detected.`
                    : isUsingDatabaseApproach
                    ? "This will be stored in the database with a client code."
                    : `This will be converted to a client ID: ${clientName
                        ? clientName.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
                        : 'example-client'}`
                }
              />

              <TextField
                label="Client Domains"
                fullWidth
                value={clientDomains}
                onChange={(e) => setClientDomains(e.target.value)}
                disabled={isCreatingClient}
                sx={{ mb: 2 }}
                helperText="Optional: Enter client domains or business areas (e.g., Healthcare, Finance, Technology)"
                multiline
                rows={2}
              />

              {/* Duplicate Warning */}
              {showDuplicateWarning && similarityMatches.length > 0 && (
                <Alert 
                  severity="warning" 
                  sx={{ mt: 2 }}
                  action={
                    <Button
                      size="small"
                      onClick={() => {
                        setShowDuplicateWarning(false);
                        setSimilarityMatches([]);
                        setClientName('');
                      }}
                    >
                      Clear
                    </Button>
                  }
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Similar clients found:
                  </Typography>
                  <Box sx={{ ml: 2 }}>
                    {similarityMatches.slice(0, 3).map((match, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                        • "{match.existingClient}" ({match.similarityScore}% similarity, {match.matchType} match)
                      </Typography>
                    ))}
                  </Box>
                </Alert>
              )}


            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Upload Data Sources
              </Typography>
              
              {createdClient && createdWorkflow && (
                <>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      <strong>Client & Workflow Context:</strong> Files will be uploaded for client "{createdClient.name}" ({createdClient.client_id}) / workflow "{createdWorkflow.workflow_name}" ({createdWorkflow.workflow_id})
                    </Typography>
                  </Alert>
                  
                  <ClientFileUpload
                    clientId={createdClient.client_id.toString()}
                    clientName={createdClient.name}
                    workflowId={createdWorkflow.workflow_id}
                  />
                </>
              )}
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Review & Complete Setup
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Setup Summary
                </Typography>
                
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Client Information:
                  </Typography>
                  <Typography variant="body2">
                    Name: {createdClient?.name}
                  </Typography>
                  {createdClient?.client_code && (
                    <Typography variant="body2">
                      Code: {createdClient.client_code}
                    </Typography>
                  )}
                  {createdClient?.domains && (
                    <Typography variant="body2">
                      Domains: {createdClient.domains}
                    </Typography>
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Workflow Information:
                  </Typography>
                  <Typography variant="body2">
                    Name: {createdWorkflow?.workflow_name}
                  </Typography>
                  <Typography variant="body2">
                    ID: {createdWorkflow?.workflow_id}
                  </Typography>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                    Uploaded Files:
                  </Typography>
                  
                  {loadingFiles ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Loading files...
                      </Typography>
                    </Box>
                  ) : uploadedFiles.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {uploadedFiles.map((file, index) => (
                        <Box 
                          key={index}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2,
                            p: 1,
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            borderRadius: 1,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          }}
                        >
                          <InsertDriveFile 
                            sx={{ 
                              color: theme.palette.primary.main,
                              fontSize: '1.2rem'
                            }} 
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {file.filename}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Category: {file.file_category} • 
                              Size: {file.file_size ? formatFileSize(file.file_size) : 'Unknown'} • 
                              Status: {file.load_status || 'Pending'}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={file.file_category}
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No files uploaded yet. Go back to the previous step to upload files.
                    </Typography>
                  )}
                </Paper>
              </Box>

              <Alert severity="info">
                Once you complete the setup, you'll be redirected to the workflow dashboard where you can begin processing your keyword data for this specific workflow.
              </Alert>
            </Box>
          )}
        </StyledPaper>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0 || isProcessing}
            sx={{ borderRadius: '8px' }}
          >
            Back
          </Button>
          
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceed() || isProcessing}
            sx={{ borderRadius: '8px' }}
            startIcon={
              isCreatingClient || isCreatingWorkflow || isProcessing ? (
                <CircularProgress size={16} />
              ) : activeStep === 2 ? (
                <Check />
              ) : (
                <Upload />
              )
            }
          >
            {activeStep === 2 ? 'Complete Setup' : 
             activeStep === 0 && createdClient && createdWorkflow ? 'Continue' :
             activeStep === 0 && createdClient ? 'Create Workflow' :
             'Next'}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ClientKeywordSetupScreen;
